import type { NorfolkSouthernTrainSelection } from '@rsos/reduxDataStore/src/features/norfolkSouthern/norfolkSouthernSlice';
import type { NorfolkSouthernConsistRequest } from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  extractErrorMessage,
  getErrorStatus,
} from '@rsos/reduxDataStore/src/utils/errorUtils';
import {
  formatInternationalPhoneNumberE164,
  isValidInternationalPhoneNumber,
} from '@rsos/utils/internationalPhoneNumberUtils';
import { isRealIsoDate } from '../../../../../utils/norfolkSouthern/trains';

export const REASON_MAX_LENGTH = 500;
export const REASON_MIN_LENGTH = 3;

export const REASON_OTHER = 'Other';

/** The reasons offered in the Reason dropdown, in display order. */
export const NORFOLK_SOUTHERN_REASONS = [
  'Train Derailment',
  'Train Derailment with Fire',
  'Train Fire',
  'Train Collision',
  REASON_OTHER,
] as const;

export interface NorfolkSouthernFormValues {
  /** Dynamic object ID of the chosen train. */
  externalId: string;
  /** The date the train departed its origin, as YYYY-MM-DD. */
  originDate: string;
  /** One of `NORFOLK_SOUTHERN_REASONS`, or empty until one is picked. */
  reason: string;
  /** What was reported to the ECC, when the reason is Other. */
  otherReason: string;
  foreignTrain: boolean;
  relatedCall: string;
}

export const NORFOLK_SOUTHERN_INITIAL_FORM_VALUES: NorfolkSouthernFormValues = {
  externalId: '',
  originDate: '',
  reason: '',
  otherReason: '',
  foreignTrain: false,
  relatedCall: '',
};

/** The reason sent to the backend: the picked reason, or the typed one for Other. */
export function resolveReason(values: NorfolkSouthernFormValues): string {
  return values.reason === REASON_OTHER
    ? values.otherReason.trim()
    : values.reason;
}

export type NorfolkSouthernFormErrors = Partial<
  Record<keyof NorfolkSouthernFormValues, string>
>;

export function validateNorfolkSouthernForm(
  values: NorfolkSouthernFormValues,
): NorfolkSouthernFormErrors {
  const errors: NorfolkSouthernFormErrors = {};

  if (!values.externalId) {
    errors.externalId = 'Select a train';
  }

  if (!values.originDate) {
    errors.originDate = 'Required';
  } else if (!isRealIsoDate(values.originDate)) {
    errors.originDate = 'Enter a valid date';
  }

  if (!values.reason) {
    errors.reason = 'Select a reason';
  } else if (values.reason === REASON_OTHER) {
    const otherReason = values.otherReason.trim();
    if (!otherReason) {
      errors.otherReason = 'Required';
    } else if (otherReason.length < REASON_MIN_LENGTH) {
      errors.otherReason = 'Describe what was reported';
    } else if (otherReason.length > REASON_MAX_LENGTH) {
      errors.otherReason = `Keep the reason under ${REASON_MAX_LENGTH} characters`;
    }
  }

  const relatedCall = values.relatedCall.trim();
  if (relatedCall && !isValidInternationalPhoneNumber(relatedCall)) {
    errors.relatedCall = 'Enter a valid phone number';
  }

  return errors;
}

/**
 * The consist request body: the train as the map has it now, or its snapshot
 * from when it was chosen if it has since left, plus what the user entered.
 */
export function buildConsistRequestBody(
  train: NorfolkSouthernTrainSelection,
  values: NorfolkSouthernFormValues,
): NorfolkSouthernConsistRequest {
  const relatedCall = values.relatedCall.trim();
  const callerId = relatedCall
    ? (formatInternationalPhoneNumberE164(relatedCall) ?? relatedCall)
    : null;

  return {
    externalId: train.externalId,
    trainId: train.trainId,
    trainSymbol: train.trainSymbol,
    originDate: values.originDate,
    position: train.position,
    speedMph: train.speedMph,
    headingDegrees: train.headingDegrees,
    numberOfCars: train.numberOfCars,
    trainLengthFeet: train.trainLengthFeet,
    status: train.status,
    reason: resolveReason(values),
    foreignTrain: values.foreignTrain,
    callerId,
  };
}

export const CONSIST_SUBMIT_ERROR_MESSAGE =
  'Unable to submit the consist request. Please try again.';

/**
 * The guardrail refusals etl_integrations answers with, keyed by their
 * stable `detail` strings, and the copy shown for each.
 */
export const CONSIST_GUARDRAIL_MESSAGES: Record<string, string> = {
  'Authority does not have the Norfolk Southern spatial layer capability.':
    'Your agency is not provisioned for Norfolk Southern consist requests. Contact RapidSOS support.',
  "Train position is not covered by the authority's jurisdiction.":
    'This train is outside your jurisdiction. Consists can only be requested for trains inside it.',
};

const VALIDATION_ERROR_MESSAGE =
  'The request was rejected as invalid. Check the train, origin date and reason and try again.';
const NOT_ENABLED_MESSAGE =
  'Norfolk Southern consist requests are not enabled on this environment.';
const NO_PERMISSION_MESSAGE =
  'You do not have permission to request a train consist.';

/**
 * User facing message for a failed consist request. Guardrail refusals map
 * to specific copy; a validation error (FastAPI 422 carries structured
 * details, not a message), a disabled integration, a missing permission and
 * a server error get generic copy, since none of their details are written
 * for telecommunicators; anything else falls back to the backend's detail
 * when it has one.
 */
export function getConsistSubmitErrorMessage(error: unknown): string {
  const status = getErrorStatus(error);
  const detail = extractErrorMessage(error, '');

  if (detail && CONSIST_GUARDRAIL_MESSAGES[detail]) {
    return CONSIST_GUARDRAIL_MESSAGES[detail];
  }
  if (status === 422) {
    return VALIDATION_ERROR_MESSAGE;
  }
  if (status === 501) {
    return NOT_ENABLED_MESSAGE;
  }
  if (status === 403) {
    return NO_PERMISSION_MESSAGE;
  }
  if (status !== undefined && status >= 500) {
    return CONSIST_SUBMIT_ERROR_MESSAGE;
  }
  return detail || CONSIST_SUBMIT_ERROR_MESSAGE;
}
